import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { styles } from './CollectionLeaderboardBanner.css';
import React, { useEffect, useRef } from 'react';
import { useTrackingContext } from '../Tracking';
import { Link } from '../PlatformFunctionality';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import type { TermsAndConditionsData } from '../TermsAndConditions/TermsAndConditionsModal';
import { TermsAndConditionsLink } from '../TermsAndConditions/TermsAndConditionsLink';

export interface CollectionLeaderboardBannerProps {
  href: string;
  desktopImage: React.ReactNode;
  mobileImage: React.ReactNode;
  trackingData?: {
    name?: string;
    isBranded?: boolean;
    category?: string;
    index?: number;
  };
  landscapeGrid?: boolean;
  termsAndConditions?: TermsAndConditionsData;
}

export const CollectionLeaderboardBanner = ({
  href,
  desktopImage,
  mobileImage,
  trackingData,
  landscapeGrid,
  termsAndConditions,
}: CollectionLeaderboardBannerProps) => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const isOnScreen = useIsOnScreen(anchorRef, { persistOnceSeen: true });
  const { tracker } = useTrackingContext();
  const isDesktop = useBreakpoint('lg', 'up');

  useEffect(() => {
    if (isOnScreen) {
      tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: anchorRef.current });
    }
  }, [isOnScreen, tracker]);

  const trackingDataToDataAttributes = () => {
    if (!trackingData) return {};
    return {
      'data-gaip': 'true',
      'data-gaip-category': trackingData.category ?? '',
      'data-gaip-is-branded': trackingData.isBranded ?? false,
      'data-gaip-type': 'leaderboard',
      'data-gaip-position': trackingData.index,
      'data-gaip-has-onclick': 'true',
      'data-gaip-url': href ?? '',
      'data-gaip-id': trackingData.name ?? '',
      'data-testid': 'collection-leaderboard-banner',
    };
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = () => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: anchorRef.current });
  };

  return (
    <div
      className={`${styles.leaderboard({ landscapeGrid })} webinventory-leaderboard`}
      data-testid="collection-leaderboard-banner"
    >
      <Link
        className={styles.linkOverlay}
        onClick={handleClick}
        ref={anchorRef}
        href={href}
        {...trackingDataToDataAttributes()}
      />
      {isDesktop ? desktopImage : mobileImage}
      {termsAndConditions ? (
        <div className={styles.termsLinkContainer({ isWhiteLink: termsAndConditions?.linkColor === 'white' })}>
          <TermsAndConditionsLink termsAndConditions={termsAndConditions} />
        </div>
      ) : null}
    </div>
  );
};
